import { getIntl } from '@umijs/max';

type MessageValues = Record<string, string | number | boolean | null>;

const AUTO_I18N_PREFIXES_WITH_FALLBACK_EN = [
  'auto.pages.pages.requirements.feature-list.index.',
  'auto.component.pages.requirements.feature-list.components.RequirementTableView.',
  'auto.component.pages.requirements.feature-list.components.RequirementMindMapView.',
  'auto.pages.pages.requirements.requirement-pool.index.',
  'auto.pages.pages.requirements.overview.index.',
  'auto.pages.pages.requirements.role-analysis.index.',
  'auto.pages.pages.requirements.concept-analysis.index.',
  'auto.pages.pages.requirements.system-integration.index.',
  'auto.pages.pages.requirements.application-analysis.index.',
  'auto.pages.pages.data-modeling.modeling-assistant.index.',
];

const EN_AUTO_PAGE_REPLACEMENTS: Array<[string, string]> = [
  ['AI辅助建模', 'AI Modeling Assistant'],
  ['建模辅助', 'Modeling Assistant'],
  ['需求池', 'Requirement Pool'],
  ['需求概要', 'Requirement Overview'],
  ['角色分析', 'Role Analysis'],
  ['概念分析', 'Concept Analysis'],
  ['系统对接', 'System Integration'],
  ['应用分析', 'Application Analysis'],
  ['参考功能项', 'referenced feature items'],
  [
    '当前未识别到项目，请先从左侧切换到一个项目',
    'No project detected. Please switch to a project from the left panel first.',
  ],
  ['项目信息已保存', 'Project information saved'],
  ['保存项目信息失败', 'Failed to save project information'],
  ['加载', 'Load'],
  ['保存', 'Save'],
  ['创建', 'Create'],
  ['删除', 'Delete'],
  ['更新', 'Update'],
  ['清空', 'Clear'],
  ['同步', 'Sync'],
  ['导入', 'Import'],
  ['导出', 'Export'],
  ['生成', 'Generate'],
  ['应用', 'Apply'],
  ['分析', 'Analyze'],
  ['拆解', 'Breakdown'],
  ['评估', 'Estimate'],
  ['估算', 'Estimate'],
  ['工时', 'Hours'],
  ['字段', 'Field'],
  ['实体', 'Entity'],
  ['模块', 'Module'],
  ['功能点', 'Feature Point'],
  ['功能', 'Feature'],
  ['项目', 'Project'],
  ['清单', 'List'],
  ['标题', 'Title'],
  ['描述', 'Description'],
  ['状态', 'Status'],
  ['操作', 'Actions'],
  ['成功', 'Success'],
  ['失败', 'Failed'],
  ['未填写', 'Not filled'],
  ['未生成', 'Not generated'],
  ['缺少', 'Missing'],
  ['请选择', 'Please select '],
  ['请输入', 'Please enter '],
  ['请先', 'Please '],
  ['当前', 'Current'],
  ['默认', 'Default'],
  ['启用', 'Enabled'],
  ['禁用', 'Disabled'],
  ['草稿', 'Draft'],
  ['进行中', 'In Progress'],
  ['已确认', 'Confirmed'],
  ['已完成', 'Completed'],
  ['概览', 'Overview'],
  ['简介', 'Summary'],
  ['来源', 'Source'],
  ['优先级', 'Priority'],
  ['高', 'High'],
  ['中', 'Medium'],
  ['低', 'Low'],
  ['其他', 'Other'],
  ['会议', 'Meeting'],
  ['电话', 'Phone'],
  ['邮件', 'Email'],
  ['微信', 'WeChat'],
  ['待处理', 'Pending'],
  ['已交付', 'Delivered'],
  ['已归档', 'Archived'],
  ['版本', 'Version'],
  ['更新时间', 'Updated At'],
  ['创建时间', 'Created At'],
  ['负责人', 'Owner'],
  ['个', ''],
  ['条', ''],
  ['项', ' item(s)'],
  ['功能清单', 'Feature List'],
  ['需求清单', 'Requirement List'],
  ['需求项', 'Requirement Item'],
  ['需求', 'Requirement'],
  ['模块', 'Module'],
  ['功能点', 'Feature Point'],
  ['功能', 'Feature'],
  ['项目', 'Project'],
  ['清单', 'List'],
  ['标题', 'Title'],
  ['描述', 'Description'],
  ['状态', 'Status'],
  ['操作', 'Actions'],
  ['保存', 'Save'],
  ['新建', 'Create'],
  ['编辑', 'Edit'],
  ['删除', 'Delete'],
  ['导入', 'Import'],
  ['导出', 'Export'],
  ['拆解', 'Breakdown'],
  ['评估', 'Estimate'],
  ['估算', 'Estimate'],
  ['工时', 'Hours'],
  ['成功', 'Success'],
  ['失败', 'Failed'],
  ['请先', 'Please '],
  ['请选择', 'Please select '],
  ['请输入', 'Please enter '],
  ['当前', 'Current'],
  ['默认', 'Default'],
  ['启用', 'Enabled'],
  ['禁用', 'Disabled'],
  ['复杂度', 'Complexity'],
  ['已完成', 'Completed'],
  ['进行中', 'In Progress'],
  ['草稿', 'Draft'],
  ['已确认', 'Confirmed'],
  ['级', ' level'],
  ['列', ' column'],
  ['行', ' row'],
  ['个', ''],
  ['条', ''],
  ['项', ' item(s)'],
  ['级', ' level'],
  ['列', ' column'],
  ['行', ' row'],
];

function isAutoKeyWithEnFallback(id: string): boolean {
  return AUTO_I18N_PREFIXES_WITH_FALLBACK_EN.some((prefix) =>
    id.startsWith(prefix),
  );
}

function fallbackTranslateAutoPageEn(text: string): string {
  let next = text;
  for (const [zh, en] of EN_AUTO_PAGE_REPLACEMENTS) {
    next = next.split(zh).join(en);
  }
  next = next
    .replace(/，/g, ', ')
    .replace(/：/g, ': ')
    .replace(/；/g, '; ')
    .replace(/。/g, '.');
  // Last-resort cleanup for still-untranslated CJK fragments in EN locale.
  next = next
    .replace(/[\u4e00-\u9fff]+/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
  return next;
}

export function t(
  id: string,
  defaultMessage?: string,
  values?: MessageValues,
): string {
  const intl = getIntl();
  if (!intl) {
    return defaultMessage || id;
  }
  const result = intl.formatMessage({ id, defaultMessage }, values);
  const locale = intl.locale || '';
  if (locale.toLowerCase().startsWith('en') && isAutoKeyWithEnFallback(id)) {
    return fallbackTranslateAutoPageEn(result);
  }
  return result;
}
